// Configuration for the Holideviser Edit trip builder sections.
// Each list-section defines its item fields; the builder renders generically from this.

export const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.round(Math.random() * 1e6)}`;

export const LIST_SECTIONS = [
  "route",
  "stays",
  "eats",
  "drinks",
  "experiences",
  "skips",
  "daily_rhythm",
  "practicals",
];

export const SECTION_ORDER = ["overview", ...LIST_SECTIONS, "budget"];

export const SECTION_META = {
  overview: { label: "Overview", blurb: "The editorial header & point of view" },
  route: { label: "Route", blurb: "How the trip flows, place to place" },
  stays: { label: "Stay", blurb: "Where they sleep" },
  eats: { label: "Eat", blurb: "Where they should eat" },
  drinks: { label: "Drink", blurb: "Bars, cafés, sundowners" },
  experiences: { label: "Do", blurb: "Experiences worth their time" },
  skips: { label: "Skip", blurb: "What to happily miss" },
  daily_rhythm: { label: "Daily Rhythm", blurb: "A gentle day-by-day shape" },
  practicals: { label: "Practicals", blurb: "Getting around, money, etc." },
  budget: { label: "Budget", blurb: "A clean customer-facing summary" },
};

// Field schema for each list section
export const LIST_FIELDS = {
  route: [
    { key: "place", label: "Place", type: "text", placeholder: "e.g. Da Nang" },
    { key: "nights", label: "Nights", type: "text", placeholder: "e.g. 3", w: "sm" },
    { key: "note", label: "Why here", type: "textarea", placeholder: "Your point of view on this stop" },
  ],
  stays: [
    { key: "name", label: "Property", type: "text", placeholder: "e.g. The Hotel Name" },
    { key: "area", label: "Area", type: "text", placeholder: "Neighbourhood", w: "sm" },
    { key: "style", label: "Style", type: "text", placeholder: "e.g. Design-led boutique", w: "sm" },
    { key: "price_note", label: "Price note", type: "text", placeholder: "e.g. ~₹18k / night", w: "sm" },
    { key: "note", label: "Why we love it", type: "textarea", placeholder: "The editorial reason" },
  ],
  eats: [
    { key: "name", label: "Place", type: "text", placeholder: "Restaurant name" },
    { key: "cuisine", label: "Cuisine", type: "text", placeholder: "e.g. Coastal Vietnamese", w: "sm" },
    { key: "area", label: "Area", type: "text", placeholder: "Neighbourhood", w: "sm" },
    { key: "meal", label: "Best for", type: "text", placeholder: "e.g. Long lunch", w: "sm" },
    { key: "note", label: "The order", type: "textarea", placeholder: "What to eat / why go" },
  ],
  drinks: [
    { key: "name", label: "Place", type: "text", placeholder: "Bar / café name" },
    { key: "type", label: "Type", type: "text", placeholder: "e.g. Rooftop bar", w: "sm" },
    { key: "area", label: "Area", type: "text", placeholder: "Neighbourhood", w: "sm" },
    { key: "note", label: "Why", type: "textarea", placeholder: "The vibe / what to order" },
  ],
  experiences: [
    { key: "name", label: "Experience", type: "text", placeholder: "e.g. Sunrise on the pass" },
    { key: "category", label: "Category", type: "text", placeholder: "e.g. Nature / Culture", w: "sm" },
    { key: "note", label: "Why it's worth it", type: "textarea", placeholder: "The editorial reason" },
  ],
  skips: [
    { key: "name", label: "What to skip", type: "text", placeholder: "e.g. The tourist market" },
    { key: "reason", label: "Why skip it", type: "textarea", placeholder: "Say it kindly but honestly" },
  ],
  daily_rhythm: [
    { key: "day_label", label: "Day", type: "text", placeholder: "e.g. Day 1", w: "sm" },
    { key: "title", label: "Title", type: "text", placeholder: "e.g. Arrive slowly" },
    { key: "morning", label: "Morning", type: "textarea", placeholder: "Loose plan for the morning" },
    { key: "afternoon", label: "Afternoon", type: "textarea", placeholder: "Afternoon" },
    { key: "evening", label: "Evening", type: "textarea", placeholder: "Evening" },
  ],
  practicals: [
    { key: "label", label: "Topic", type: "text", placeholder: "e.g. Getting around", w: "sm" },
    { key: "detail", label: "Detail", type: "textarea", placeholder: "Your advice" },
  ],
};

// Short title used on collapsed item headers
export const ITEM_TITLE = {
  route: (it) => it.place || "New stop",
  stays: (it) => it.name || "New stay",
  eats: (it) => it.name || "New spot",
  drinks: (it) => it.name || "New spot",
  experiences: (it) => it.name || "New experience",
  skips: (it) => it.name || "New skip",
  daily_rhythm: (it) => [it.day_label, it.title].filter(Boolean).join(" — ") || "New day",
  practicals: (it) => it.label || "New note",
};

export const newItem = (section) => {
  const item = { id: uid() };
  (LIST_FIELDS[section] || []).forEach((f) => (item[f.key] = ""));
  return item;
};
